import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ALL_BUILDINGS,
  ALL_COURSES,
  FALLBACK_PLACES,
  GOOGLE_MAPS_API_KEY,
} from "../constants/studyBuddyData";

const DEFAULT_DATA = {
  type: "",
  visibility: "",
  courseMode: "",
  courses: [],
  locType: "",
  location: null,
  timeMode: "",
  date: "",
  start: "",
  end: "",
  maxMembers: 5,
  genderPref: "any",
};

export default function CreateSessionModal({
  isOpen,
  onClose,
  onPersistSession,
  onSessionCreated,
  accessToken,
  onSignIn,
  createGoogleMeetLink,
}) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(DEFAULT_DATA);
  const [search, setSearch] = useState("");
  const [dropdown, setDropdown] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showHoursPanel, setShowHoursPanel] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const ref = useRef(null);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const selectedMarkerRef = useRef(null);
  const placesLoadedRef = useRef(false);

  const reset = useCallback(() => {
    setStep(1);
    setData(DEFAULT_DATA);
    setSearch("");
    setShowMap(false);
    setSelectedPlace(null);
    setShowHoursPanel(false);
    setShowLocationPopup(false);
    placesLoadedRef.current = false;
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
      selectedMarkerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (!window.google?.maps) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => setMapLoaded(true);
      script.onerror = () => setMapLoaded(false);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, [isOpen]);

  const requestLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLocationGranted(true);
          resolve(true);
        },
        () => {
          setLocationGranted(false);
          resolve(false);
        }
      );
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    requestLocation();
  }, [isOpen, requestLocation]);

  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const radius = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2
      + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
      * Math.sin(dLng / 2) ** 2;
    return (radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1);
  }, []);

  const placeSelectionPin = useCallback((place) => {
    if (!googleMapRef.current || !window.google?.maps) return;
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
    }
    const marker = new window.google.maps.Marker({
      position: { lat: place.lat, lng: place.lng },
      map: googleMapRef.current,
      title: place.name,
      animation: window.google.maps.Animation.BOUNCE,
      icon: {
        url: `data:image/svg+xml,${encodeURIComponent('<svg width="44" height="54" xmlns="http://www.w3.org/2000/svg"><path d="M22 0C12.3 0 4.4 7.9 4.4 17.6c0 9.7 17.6 36.4 17.6 36.4s17.6-26.7 17.6-36.4C39.6 7.9 31.7 0 22 0z" fill="#DC2626"/><circle cx="22" cy="17.6" r="8.8" fill="white"/><text x="22" y="22" text-anchor="middle" font-size="14" fill="#DC2626">★</text></svg>')}`,
        scaledSize: new window.google.maps.Size(44, 54),
        anchor: new window.google.maps.Point(22, 54),
      },
    });
    selectedMarkerRef.current = marker;
    setTimeout(() => marker?.setAnimation(null), 1400);
    googleMapRef.current.panTo({ lat: place.lat, lng: place.lng });
    googleMapRef.current.setZoom(16);
  }, []);

  const loadFallbackPlaces = useCallback(() => {
    const hour = new Date().getHours();
    const places = FALLBACK_PLACES.map((place) => {
      let busy = "Moderate";
      if (hour >= 8 && hour <= 11) {
        busy = ["Busy", "Moderate", "Quiet"][Math.floor(Math.random() * 3)];
      } else if (hour >= 12 && hour <= 14) {
        busy = "Busy";
      } else if (hour >= 15 && hour <= 17) {
        busy = ["Busy", "Moderate"][Math.floor(Math.random() * 2)];
      } else {
        busy = "Quiet";
      }
      return { ...place, busy };
    });
    setNearbyPlaces(places);
    placesLoadedRef.current = true;
  }, []);

  const fetchNearbyPlaces = useCallback((map) => {
    if (!window.google?.maps?.places || !userLocation) {
      loadFallbackPlaces();
      return;
    }
    const service = new window.google.maps.places.PlacesService(map);
    const request = {
      location: new window.google.maps.LatLng(userLocation.lat, userLocation.lng),
      radius: 2000,
      type: ["cafe"],
      keyword: "coffee study wifi",
    };
    const timeout = setTimeout(() => {
      if (!placesLoadedRef.current) loadFallbackPlaces();
    }, 5000);

    service.nearbySearch(request, (results, status) => {
      clearTimeout(timeout);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length > 0) {
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];
        const places = results.slice(0, 15).map((place) => {
          const dist = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            place.geometry.location.lat(),
            place.geometry.location.lng()
          );
          const hour = new Date().getHours();
          let busy = "Moderate";
          if (hour >= 8 && hour <= 11) busy = ["Busy", "Moderate", "Quiet"][Math.floor(Math.random() * 3)];
          else if (hour >= 12 && hour <= 14) busy = "Busy";
          else if (hour >= 15 && hour <= 17) busy = ["Busy", "Moderate"][Math.floor(Math.random() * 2)];
          else busy = "Quiet";

          const placeData = {
            id: place.place_id,
            name: place.name,
            address: place.vicinity,
            rating: place.rating || 4.0,
            totalRatings: place.user_ratings_total || 0,
            busy,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            placeId: place.place_id,
            distance: dist,
            priceLevel: place.price_level || 2,
            openNow: place.opening_hours?.open_now,
            photos: place.photos,
          };

          const marker = new window.google.maps.Marker({
            position: { lat: placeData.lat, lng: placeData.lng },
            map,
            title: placeData.name,
            animation: window.google.maps.Animation.DROP,
            icon: {
              url: `data:image/svg+xml,${encodeURIComponent('<svg width="40" height="50" xmlns="http://www.w3.org/2000/svg"><path d="M20 0C11.2 0 4 7.2 4 16c0 8.8 16 34 16 34s16-25.2 16-34c0-8.8-7.2-16-16-16z" fill="#6366F1"/><circle cx="20" cy="16" r="8" fill="white"/><text x="20" y="21" text-anchor="middle" font-size="12" fill="#6366F1" font-weight="bold">☕</text></svg>')}`,
              scaledSize: new window.google.maps.Size(40, 50),
              anchor: new window.google.maps.Point(20, 50),
            },
          });

          marker.addListener("click", () => {
            setSelectedPlace(placeData);
            setShowHoursPanel(false);
            placeSelectionPin(placeData);
            service.getDetails(
              {
                placeId: placeData.placeId,
                fields: [
                  "opening_hours",
                  "website",
                  "formatted_phone_number",
                  "reviews",
                ],
              },
              (details, statusDetails) => {
                if (statusDetails === window.google.maps.places.PlacesServiceStatus.OK) {
                  setSelectedPlace((prev) => ({
                    ...prev,
                    hours: details.opening_hours?.weekday_text,
                    website: details.website,
                    phone: details.formatted_phone_number,
                    reviews: details.reviews?.slice(0, 3),
                  }));
                }
              }
            );
          });

          markersRef.current.push(marker);
          return placeData;
        });

        setNearbyPlaces(places.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance)));
        placesLoadedRef.current = true;
      } else {
        loadFallbackPlaces();
      }
    });
  }, [calculateDistance, loadFallbackPlaces, placeSelectionPin, userLocation]);

  useEffect(() => {
    if (!showMap || !userLocation) return;
    if (!placesLoadedRef.current) loadFallbackPlaces();
    if (mapLoaded && window.google?.maps && mapRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: userLocation,
        zoom: 14,
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        ],
      });
      googleMapRef.current = map;
      new window.google.maps.Marker({
        position: userLocation,
        map,
        title: "You",
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#4F46E5",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
      });
      fetchNearbyPlaces(map);
    }
  }, [fetchNearbyPlaces, loadFallbackPlaces, mapLoaded, showMap, userLocation]);

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = ALL_COURSES.filter(
    (course) => course.code.toLowerCase().includes(search.toLowerCase())
      || course.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleCourse = (course) => {
    if (data.courseMode === "single") {
      setData({ ...data, courses: [course] });
      setDropdown(false);
    } else {
      const exists = data.courses.find((item) => item.code === course.code);
      setData({
        ...data,
        courses: exists
          ? data.courses.filter((item) => item.code !== course.code)
          : [...data.courses, course],
      });
    }
  };

  const canNext = () => {
    if (step === 1) return data.type;
    if (step === 2) return data.visibility;
    if (step === 3) return data.courseMode === "flex" || data.courses.length > 0;
    if (step === 4) return data.type === "online" || data.location;
    if (step === 5) {
      if (!data.timeMode) return false;
      if (data.timeMode === "scheduled") return data.date && data.start && data.end;
      return true;
    }
    return true;
  };

  const openNearbySpots = async () => {
    setData((prev) => ({ ...prev, locType: "spot" }));
    if (locationGranted && userLocation) {
      setShowMap(true);
    } else {
      const granted = await requestLocation();
      if (granted) {
        setShowMap(true);
      } else {
        setShowLocationPopup(true);
      }
    }
  };

  const selectPlaceFromMap = (place) => {
    setData({ ...data, location: place });
    setShowMap(false);
    setSelectedPlace(null);
    setShowHoursPanel(false);
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.setMap(null);
      selectedMarkerRef.current = null;
    }
  };

  const BusyBadge = ({ busy }) => (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${busy === "Quiet" ? "bg-green-100 text-green-700" : busy === "Moderate" ? "bg-yellow-100 text-yellow-700" : "bg-orange-100 text-orange-700"}`}
    >
      {busy}
    </span>
  );

  const handleCreateSession = async () => {
    const courseName = data.courseMode === "flex"
      ? "Flexible"
      : data.courses.length > 0
        ? data.courses.map((course) => course.code).join(", ")
        : "Study";

    const sessionToSave = {
      type: data.type,
      visibility: data.visibility,
      course: courseName,
      note: data.note || "",
      when: data.timeMode === "now"
        ? "now"
        : data.timeMode === "scheduled"
          ? `${data.date} ${data.start} - ${data.end}`
          : "flexible",
      max_size: data.maxMembers,
      gender: data.genderPref,
      created_by: "Maria",
    };

    try {
      await onPersistSession(sessionToSave);
    } catch (error) {
      console.error("Error creating session:", error);
      return;
    }

    let meetLink = null;
    console.log("Creating session - type:", data.type, "accessToken:", !!accessToken, "createGoogleMeetLink:", !!createGoogleMeetLink);
    if (data.type === "online" && accessToken && createGoogleMeetLink) {
      console.log("Attempting to create Google Meet link...");
      meetLink = await createGoogleMeetLink();
      console.log("Meet link result:", meetLink);
    } else if (data.type === "online" && !accessToken) {
      console.log("No accessToken - user not signed in with Google");
    }

    const localSession = {
      id: Date.now(),
      course: data.courseMode === "flex" ? "Flexible" : data.courses[0]?.code || "Study",
      name: data.courseMode === "flex" ? "General Study Session" : data.courses.map((course) => course.code).join(", "),
      location: data.type === "online" ? "Online" : data.location?.name,
      time: data.timeMode === "now" ? "LIVE NOW" : data.timeMode === "scheduled" ? `${data.date} ${data.start}` : "Flexible",
      members: 1,
      maxMembers: data.maxMembers,
      organizer: "You",
      meetLink,
      genderPref: data.genderPref,
      isLive: data.timeMode === "now",
    };

    onSessionCreated(localSession);
    if (meetLink) window.open(meetLink, "_blank");
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] flex flex-col my-4">
        <div className="p-6 border-b flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-bold">
              {showMap
                ? "Choose Study Spot"
                : [
                  "Session Type",
                  "Who Can Join?",
                  "Course Selection",
                  "Study Location",
                  "When to Study?",
                  "Group Details",
                ][step - 1]}
            </h2>
            {!showMap && <p className="text-sm text-gray-500">Step {step} of 6</p>}
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {!showMap && (
          <div className="px-6 py-3 bg-gray-50 shrink-0">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all"
                style={{ width: `${(step / 6) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {!showMap && step === 1 && (
            <div className="p-6 space-y-4">
              <p className="text-gray-600 mb-6">How would you like to study?</p>
              <div className="grid grid-cols-2 gap-4">
                {["online", "in-person"].map((type) => (
                  <button
                    key={type}
                    onClick={() => setData({ ...data, type })}
                    className={`p-6 rounded-xl border-2 transition-all ${data.type === type ? "border-indigo-600 bg-indigo-50 scale-105" : "border-gray-200"}`}
                  >
                    <div className="text-4xl mb-3">{type === "online" ? "💻" : "📍"}</div>
                    <div className="font-bold capitalize">{type.replace("-", " ")}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {type === "online" ? "Google Meet" : "Meet at location"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showMap && step === 2 && (
            <div className="p-6 space-y-4">
              <p className="text-gray-600 mb-6">Who can see and join?</p>
              <div className="grid grid-cols-2 gap-4">
                {["public", "private"].map((visibility) => (
                  <button
                    key={visibility}
                    onClick={() => setData({ ...data, visibility })}
                    className={`p-6 rounded-xl border-2 transition-all ${data.visibility === visibility ? "border-indigo-600 bg-indigo-50 scale-105" : "border-gray-200"}`}
                  >
                    <div className="text-4xl mb-3">{visibility === "public" ? "🌍" : "🔒"}</div>
                    <div className="font-bold capitalize">{visibility}</div>
                    <div className="text-sm text-gray-500 mt-1">
                      {visibility === "public" ? "Everyone" : "Friends only"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showMap && step === 3 && (
            <div className="p-6 space-y-6">
              <div>
                <p className="text-gray-600 mb-4">What are you studying?</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { mode: "single", label: "One Course", icon: "📖" },
                    { mode: "multi", label: "Multiple", icon: "📚" },
                    { mode: "flex", label: "Flexible", icon: "✨" },
                  ].map(({ mode, label, icon }) => (
                    <button
                      key={mode}
                      onClick={() => setData({
                        ...data,
                        courseMode: mode,
                        courses: mode === "flex" ? [] : data.courses.slice(0, mode === "single" ? 1 : 999),
                      })}
                      className={`p-4 rounded-xl border-2 ${data.courseMode === mode ? "border-indigo-600 bg-indigo-50" : "border-gray-200"}`}
                    >
                      <div className="text-3xl mb-2">{icon}</div>
                      <div className="text-sm font-medium">{label}</div>
                    </button>
                  ))}
                </div>
              </div>
              {data.courseMode && data.courseMode !== "flex" && (
                <div ref={ref}>
                  {data.courses.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {data.courses.map((course) => (
                        <div
                          key={course.code}
                          className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full flex items-center gap-2"
                        >
                          <span className="text-sm font-medium">{course.code}</span>
                          <button
                            onClick={() => setData({
                              ...data,
                              courses: data.courses.filter((item) => item.code !== course.code),
                            })}
                            className="hover:bg-indigo-200 rounded-full text-lg leading-none"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Search courses (e.g., COMP 202)"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onFocus={() => setDropdown(true)}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-600 outline-none"
                  />
                  {dropdown && (
                    <div className="mt-2 bg-white border-2 rounded-xl max-h-80 overflow-y-auto shadow-xl">
                      {filtered.length > 0 ? (
                        filtered.map((course) => (
                          <button
                            key={course.code}
                            onClick={() => toggleCourse(course)}
                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-0 ${data.courses.find((item) => item.code === course.code) ? "bg-indigo-50" : ""}`}
                          >
                            <div className="font-semibold">{course.code}</div>
                            <div className="text-sm text-gray-600">{course.name}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-8 text-center text-gray-500">No courses found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {data.courseMode === "flex" && (
                <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-6">
                  <p className="text-indigo-900 font-semibold mb-2">✨ Flexible Matching</p>
                  <p className="text-sm text-indigo-700">You'll be matched with anyone looking to study!</p>
                </div>
              )}
            </div>
          )}

          {!showMap && step === 4 && (
            <div className="p-6 space-y-6">
              {data.type === "online" ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">💻</div>
                  <h3 className="text-xl font-bold mb-2">Online Session</h3>
                  {accessToken ? (
                    <div>
                      <p className="text-green-600 font-medium">✓ Signed in with Google</p>
                      <p className="text-gray-600 mt-2">A Google Meet link will be created when you finish</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-600 mb-2">Connect Google to auto-generate a Meet link</p>
                      <p className="text-sm text-gray-400 mb-4">You can also skip this and share a link later</p>
                      <button
                        onClick={onSignIn}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 rounded-xl font-medium hover:bg-gray-50 shadow-sm"
                      >
                        <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                        Sign in with Google
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-gray-600 mb-4">Where do you want to study?</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { type: "spot", label: "Nearby Spots", icon: "🗺️" },
                        { type: "building", label: "McGill Building", icon: "🏛️" },
                      ].map(({ type, label, icon }) => (
                        <button
                          key={type}
                          onClick={() => {
                            if (type === "spot") openNearbySpots();
                            else setData({ ...data, locType: type });
                          }}
                          className={`p-4 rounded-xl border-2 ${data.locType === type ? "border-indigo-600 bg-indigo-50" : "border-gray-200"}`}
                        >
                          <div className="text-3xl mb-2">{icon}</div>
                          <div className="text-sm font-medium">{label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {data.locType === "building" && (
                    <div className="space-y-3">
                      {ALL_BUILDINGS.map((building) => (
                        <div key={building.id}>
                          <button
                            onClick={() => setData({ ...data, location: building })}
                            className={`w-full p-4 rounded-xl border-2 text-left ${data.location?.id === building.id ? "border-indigo-600 bg-indigo-50" : "border-gray-200"}`}
                          >
                            <div className="font-semibold">{building.name}</div>
                            {building.booking && <div className="text-sm text-indigo-600 mt-1">✓ Room booking available</div>}
                          </button>
                          {data.location?.id === building.id && building.booking && (
                            <div className="mt-2 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                              <p className="text-sm text-yellow-900 font-medium mb-2">💡 Want to book a room?</p>
                              <a
                                href={building.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700"
                              >
                                Book Room at {building.name} →
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {data.location && data.locType === "spot" && (
                    <div className="p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-indigo-900">✓ Selected: {data.location.name}</p>
                          <p className="text-sm text-indigo-700 mt-1">{data.location.address}</p>
                          <p className="text-sm text-indigo-600 mt-1">📍 {data.location.distance} km away</p>
                        </div>
                        <button
                          onClick={openNearbySpots}
                          className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {showMap && (
            <div className="flex h-[600px]">
              <div className="flex-1 relative">
                {mapLoaded ? (
                  <div ref={mapRef} className="w-full h-full" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-5xl mb-4">🗺️</div>
                      <p className="text-gray-600 font-medium">Map view unavailable</p>
                      <p className="text-sm text-gray-500 mt-1">Select a spot from the list →</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="w-96 bg-white border-l overflow-y-auto">
                {selectedPlace ? (
                  <div className="p-6">
                    <button
                      onClick={() => {
                        setSelectedPlace(null);
                        setShowHoursPanel(false);
                        if (selectedMarkerRef.current) {
                          selectedMarkerRef.current.setMap(null);
                          selectedMarkerRef.current = null;
                        }
                      }}
                      className="text-indigo-600 hover:underline mb-4 flex items-center gap-1"
                    >
                      ← Back to list
                    </button>
                    {selectedPlace.photos?.[0] && (
                      <img
                        src={selectedPlace.photos[0].getUrl({ maxWidth: 400 })}
                        alt={selectedPlace.name}
                        className="w-full h-48 object-cover rounded-xl mb-4"
                      />
                    )}
                    <h3 className="text-xl font-bold mb-2">{selectedPlace.name}</h3>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2"><span className="text-yellow-500">⭐</span><span className="font-medium">{selectedPlace.rating}</span>{selectedPlace.totalRatings > 0 && <span className="text-sm text-gray-500">({selectedPlace.totalRatings} reviews)</span>}</div>
                      <div className="flex items-center gap-2"><span>📍</span><span className="text-sm">{selectedPlace.address}</span></div>
                      <div className="flex items-center gap-2"><span>🚶</span><span className="text-sm">{selectedPlace.distance} km away</span></div>
                      <div className="flex items-center gap-2"><span>💰</span><span className="text-sm">{"$".repeat(selectedPlace.priceLevel || 2)}</span></div>
                      <div className="flex items-center gap-2"><span>👥</span><BusyBadge busy={selectedPlace.busy} /></div>
                      {selectedPlace.openNow !== undefined && (
                        <div className="flex items-center gap-2"><span>🕐</span><span className={`text-sm font-medium ${selectedPlace.openNow ? "text-green-600" : "text-red-600"}`}>{selectedPlace.openNow ? "Open now" : "Closed"}</span></div>
                      )}
                    </div>

                    {selectedPlace.hours && selectedPlace.hours.length > 0 && (
                      <div className="mb-4">
                        {!showHoursPanel ? (
                          <button
                            onClick={() => setShowHoursPanel(true)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
                          >
                            🕐 See opening hours ▾
                          </button>
                        ) : (
                          <div className="bg-gray-50 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-semibold text-sm">Opening Hours</h4>
                              <button onClick={() => setShowHoursPanel(false)} className="text-xs text-gray-500 hover:text-gray-700">Hide ▴</button>
                            </div>
                            <div className="text-sm space-y-1 text-gray-600">
                              {selectedPlace.hours.map((hour, i) => {
                                const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                                const today = days[new Date().getDay()];
                                const isToday = hour.startsWith(today);
                                return (
                                  <div key={i} className={isToday ? "font-semibold text-indigo-700" : ""}>
                                    {hour}{isToday ? " ← today" : ""}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedPlace.reviews?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3">Recent Reviews</h4>
                        <div className="space-y-3">
                          {selectedPlace.reviews.map((review, i) => (
                            <div key={i} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center gap-2 mb-1"><span className="text-yellow-500">⭐</span><span className="font-medium text-sm">{review.rating}/5</span></div>
                              <p className="text-sm text-gray-600 line-clamp-3">{review.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => selectPlaceFromMap(selectedPlace)}
                      className="w-full px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700"
                    >
                      Select This Location
                    </button>
                  </div>
                ) : (
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-4">Nearby Study Spots</h3>
                    <p className="text-sm text-gray-500 mb-4">{mapLoaded ? "Click a marker on the map or select from the list" : "Select a spot from the list"}</p>
                    <div className="space-y-3">
                      {nearbyPlaces.length > 0 ? nearbyPlaces.map((place) => (
                        <button
                          key={place.id}
                          onClick={() => {
                            setSelectedPlace(place);
                            setShowHoursPanel(false);
                            placeSelectionPin(place);
                          }}
                          className="w-full p-4 rounded-xl border-2 border-gray-200 text-left hover:border-indigo-600 hover:bg-indigo-50 transition-all"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="font-semibold">{place.name}</div>
                              <div className="text-xs text-gray-500 mt-1">{place.address}</div>
                            </div>
                            <div className="text-sm flex items-center gap-1"><span className="text-yellow-500">⭐</span><span>{place.rating}</span></div>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-gray-600">📍 {place.distance} km</span>
                            <span className={`px-2 py-1 rounded-md font-medium ${place.busy === "Quiet" ? "bg-green-50 text-green-700" : place.busy === "Moderate" ? "bg-yellow-50 text-yellow-700" : "bg-orange-50 text-orange-700"}`}>{place.busy}</span>
                          </div>
                        </button>
                      )) : (
                        <div className="text-center py-8 text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                          <p>Loading nearby places...</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!showMap && step === 5 && (
            <div className="p-6 space-y-3">
              <p className="text-gray-600 mb-4">When do you want to study?</p>
              {[
                { mode: "now", label: "Right Now", sub: "Start immediately", icon: "⚡" },
                { mode: "flexible", label: "Flexible", sub: "Any time this week", icon: "🕐" },
                { mode: "scheduled", label: "Schedule", sub: "Pick a date & time", icon: "📅" },
              ].map(({ mode, label, sub, icon }) => (
                <div key={mode}>
                  <button
                    onClick={() => setData({ ...data, timeMode: mode })}
                    className={`w-full p-5 rounded-xl border-2 text-left ${data.timeMode === mode ? "border-indigo-600 bg-indigo-50" : "border-gray-200"}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{icon}</div>
                      <div>
                        <div className="font-bold">{label}</div>
                        <div className="text-sm text-gray-500">{sub}</div>
                      </div>
                    </div>
                  </button>
                  {mode === "scheduled" && data.timeMode === "scheduled" && (
                    <div className="mt-2 p-5 bg-gray-50 rounded-xl space-y-4 border-2 border-gray-100">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
                        <input
                          type="date"
                          value={data.date}
                          onChange={(event) => setData({ ...data, date: event.target.value })}
                          className="w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-600 outline-none"
                          min={new Date().toISOString().split("T")[0]}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Start time</label>
                          <input
                            type="time"
                            value={data.start}
                            onChange={(event) => setData({ ...data, start: event.target.value })}
                            className="w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-600 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">End time</label>
                          <input
                            type="time"
                            value={data.end}
                            onChange={(event) => setData({ ...data, end: event.target.value })}
                            className="w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-600 outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!showMap && step === 6 && (
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Maximum Group Size</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="2"
                    max="50"
                    value={data.maxMembers}
                    onChange={(event) => {
                      const value = parseInt(event.target.value, 10);
                      if (!Number.isNaN(value) && value >= 1 && value <= 50) {
                        setData({ ...data, maxMembers: value });
                      } else if (event.target.value === "") {
                        setData({ ...data, maxMembers: "" });
                      }
                    }}
                    onBlur={(event) => {
                      const value = parseInt(event.target.value, 10);
                      if (Number.isNaN(value) || value < 2) setData({ ...data, maxMembers: 2 });
                      else if (value > 50) setData({ ...data, maxMembers: 50 });
                    }}
                    placeholder="e.g. 5"
                    className="w-32 px-4 py-3 border-2 rounded-xl text-center text-lg font-bold focus:border-indigo-600 outline-none"
                  />
                  <span className="text-sm text-gray-500">people (2–50)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Gender Preference (Optional)</label>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { val: "any", label: "Any", icon: "👥" },
                    { val: "male", label: "Male", icon: "👨" },
                    { val: "female", label: "Female", icon: "👩" },
                    { val: "non-binary", label: "Non-binary", icon: "🧑" },
                  ].map(({ val, label, icon }) => (
                    <button
                      key={val}
                      onClick={() => setData({ ...data, genderPref: val })}
                      className={`p-4 rounded-xl border-2 ${data.genderPref === val ? "border-indigo-600 bg-indigo-50" : "border-gray-200"}`}
                    >
                      <div className="text-2xl mb-1">{icon}</div>
                      <div className="text-xs font-medium">{label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-100">
                <h4 className="font-bold mb-4">📋 Session Summary</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-600">Type:</span> <span className="font-medium capitalize">{data.type?.replace("-", " ")}</span></div>
                  <div><span className="text-gray-600">Visibility:</span> <span className="font-medium capitalize">{data.visibility}</span></div>
                  <div><span className="text-gray-600">Courses:</span> <span className="font-medium">{data.courseMode === "flex" ? "Flexible" : data.courses.map((course) => course.code).join(", ")}</span></div>
                  {data.location && <div><span className="text-gray-600">Location:</span> <span className="font-medium">{data.location.name}</span></div>}
                  {data.type === "online" && !accessToken && <div><span className="text-gray-600">Note:</span> <span className="font-medium text-amber-600">No Google Meet — share a link manually</span></div>}
                  {data.type === "online" && accessToken && <div><span className="text-gray-600">Meet:</span> <span className="font-medium text-green-600">Auto-generated Google Meet link</span></div>}
                  <div><span className="text-gray-600">When:</span> <span className="font-medium capitalize">{data.timeMode}</span></div>
                  <div><span className="text-gray-600">Max Size:</span> <span className="font-medium">{data.maxMembers} people</span></div>
                  <div><span className="text-gray-600">Gender:</span> <span className="font-medium capitalize">{data.genderPref}</span></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!showMap && (
          <div className="p-6 border-t bg-gray-50 flex justify-between shrink-0">
            <button
              onClick={step === 1 ? handleClose : () => setStep(step - 1)}
              className="px-5 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
            >
              {step === 1 ? "Cancel" : "← Back"}
            </button>
            {step < 6 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canNext()}
                className={`px-6 py-2 rounded-lg font-medium ${canNext() ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-gray-200 text-gray-400"}`}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleCreateSession}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700"
              >
                Create Session
              </button>
            )}
          </div>
        )}
        {showMap && (
          <div className="p-6 border-t bg-gray-50 flex justify-between shrink-0">
            <button
              onClick={() => {
                setShowMap(false);
                setSelectedPlace(null);
                setShowHoursPanel(false);
                if (selectedMarkerRef.current) {
                  selectedMarkerRef.current.setMap(null);
                  selectedMarkerRef.current = null;
                }
              }}
              className="px-5 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
            >
              ← Back
            </button>
            {selectedPlace && (
              <button
                onClick={() => selectPlaceFromMap(selectedPlace)}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
              >
                Select Location
              </button>
            )}
          </div>
        )}
      </div>

      {showLocationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-2xl">
            <div className="text-5xl mb-4">📍</div>
            <h3 className="text-lg font-bold mb-2">Location Access Required</h3>
            <p className="text-gray-600 text-sm mb-2">
              To find nearby study spots, StudyBuddy needs access to your location.
            </p>
            <p className="text-gray-500 text-xs mb-6">
              Please enable location access in your browser settings and try again.
            </p>
            <div className="space-y-2">
              <button
                onClick={async () => {
                  const granted = await requestLocation();
                  if (granted) {
                    setShowLocationPopup(false);
                    setShowMap(true);
                  }
                }}
                className="w-full px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700"
              >
                Try Again
              </button>
              <button
                onClick={() => setShowLocationPopup(false)}
                className="w-full px-4 py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
